/**
 * ============================================================
 * KEJJA OS — src/pages/meals/TodayPage.tsx
 * Meals dashboard — "what do we eat and what do we need"
 * ============================================================
 */

import { Link } from 'react-router-dom';
import {
  Sparkles, ChefHat, ShoppingCart, Coffee, Utensils,
  TrendingUp, Flame,
} from 'lucide-react';

import { useMealsContext } from './MealsLayout';
import { useMealsPulse, useSeedRecipes } from '@/hooks/useMeals';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { formatMoney } from '@/lib/formatMoney';
import { cn } from '@/lib/utils';

const MEAL_ICON = {
  breakfast: Coffee,
  lunch: Utensils,
  dinner: ChefHat,
  snack: Sparkles,
} as const;

const MEAL_LABEL = {
  breakfast: 'Breakfast',
  lunch: 'Lunch',
  dinner: 'Dinner',
  snack: 'Snack',
} as const;

export default function TodayPage() {
  const { homeId } = useMealsContext();
  const { data: pulse, isLoading } = useMealsPulse(homeId);
  const seedRecipes = useSeedRecipes(homeId);

  if (isLoading || !pulse) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-40 rounded-2xl" />
        <Skeleton className="h-24 rounded-xl" />
        <Skeleton className="h-40 rounded-xl" />
      </div>
    );
  }

  // Empty state: no recipes + no plan + no shopping items
  const isEmpty = pulse.recipes.total === 0 && pulse.plan.plannedSlots === 0 && pulse.shopping.pendingCount === 0;
  if (isEmpty) {
    return (
      <Card className="border-dashed border-amber-500/30 bg-gradient-to-br from-amber-950/20 to-orange-950/10">
        <CardContent className="p-8 text-center">
          <div className="w-14 h-14 rounded-2xl bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 mx-auto flex items-center justify-center mb-4">
            <ChefHat className="w-6 h-6" />
          </div>
          <h3 className="font-semibold text-lg text-foreground">Get your kitchen set up</h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
            We'll add 7 Kenyan starter recipes (Ugali, Pilau, Chapati, Mandazi, Githeri, Mukimo, Mbuzi choma).
            You can edit, add ingredients, or remove them later.
          </p>
          <Button
            size="lg"
            className="mt-5 bg-amber-600 hover:bg-amber-700 text-white"
            onClick={() => seedRecipes.mutate(false)}
            disabled={seedRecipes.isPending}
          >
            {seedRecipes.isPending ? 'Setting up…' : 'Add starter recipes'}
          </Button>
          <p className="text-[11px] text-muted-foreground mt-4">
            Or skip and add your own:{' '}
            <Link to="/meals/recipes" className="underline font-medium">Recipes</Link>{' · '}
            <Link to="/meals/plan" className="underline font-medium">Plan</Link>{' · '}
            <Link to="/meals/shopping" className="underline font-medium">Shopping</Link>
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-5 pb-12">
      {/* ============ TODAY HERO ============ */}
      <TodayHero pulse={pulse} />

      {/* ============ STATS ROW ============ */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          label="Planned"
          value={`${pulse.plan.plannedSlots}/${pulse.plan.totalMainSlots}`}
          sub={`${pulse.plan.percentPlanned}% this week`}
          accent="amber"
        />
        <StatCard
          label="Shopping"
          value={`${pulse.shopping.pendingCount}`}
          sub={`${pulse.shopping.pendingUnassigned} unassigned`}
          accent="orange"
        />
        <StatCard
          label="Recipes"
          value={`${pulse.recipes.total}`}
          sub={`${pulse.recipes.favorites} favorites`}
          accent="rose"
        />
        {pulse.groceryFinance.enabled ? (
          <StatCard
            label="Groceries"
            value={formatMoney(pulse.groceryFinance.weekSpend, pulse.groceryFinance.currencyCode)}
            sub="this week"
            accent="emerald"
            linkTo="/finance/transactions"
          />
        ) : (
          <StatCard
            label="Bought"
            value={`${pulse.shopping.boughtThisWeek}`}
            sub="items this week"
            accent="emerald"
          />
        )}
      </div>

      {/* ============ Shopping snapshot ============ */}
      {pulse.shopping.pendingCount > 0 && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <ShoppingCart className="w-3.5 h-3.5" /> Need to buy
            </h2>
            <Link to="/meals/shopping" className="text-xs font-medium text-amber-700 dark:text-amber-300 hover:underline">
              Full list →
            </Link>
          </div>
          <Card>
            <CardContent className="p-0 divide-y divide-border">
              {pulse.shopping.topPending.map((item) => (
                <div key={item.id} className="p-3 flex items-center gap-3">
                  <div className="h-8 w-8 rounded-lg bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 flex items-center justify-center flex-shrink-0">
                    <ShoppingCart className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm truncate">{item.name}</span>
                      {!item.assigneeUserId && (
                        <Badge variant="outline" className="text-[9px] h-4 px-1 font-normal">unassigned</Badge>
                      )}
                    </div>
                    {item.quantity && (
                      <div className="text-[11px] text-muted-foreground mt-0.5">{item.quantity}</div>
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </section>
      )}

      {/* ============ Finance integration strip ============ */}
      {pulse.groceryFinance.enabled && pulse.groceryFinance.weekSpend > 0 && (
        <section>
          <Card className="border-emerald-200 dark:border-emerald-900/40 bg-gradient-to-br from-emerald-50/40 to-transparent dark:from-emerald-950/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" /> Groceries this week (from Finance)
                  </div>
                  <div className="text-xl font-bold tabular-nums text-foreground mt-1">
                    {formatMoney(pulse.groceryFinance.weekSpend, pulse.groceryFinance.currencyCode)}
                  </div>
                </div>
                <Link
                  to="/finance/transactions"
                  className="text-xs font-medium text-amber-700 dark:text-amber-300 hover:underline flex-shrink-0"
                >
                  See txns →
                </Link>
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      {/* ============ Quick actions ============ */}
      <div className="grid grid-cols-2 gap-3 pt-2">
        <Button asChild variant="outline" className="h-auto py-3 justify-start">
          <Link to="/meals/plan">
            <CalendarIcon /> <div className="text-left ml-2">
              <div className="text-sm font-medium">Plan the week</div>
              <div className="text-[11px] text-muted-foreground">Assign meals to days</div>
            </div>
          </Link>
        </Button>
        <Button asChild variant="outline" className="h-auto py-3 justify-start">
          <Link to="/meals/recipes">
            <BookIcon /> <div className="text-left ml-2">
              <div className="text-sm font-medium">Add a recipe</div>
              <div className="text-[11px] text-muted-foreground">Save a family favorite</div>
            </div>
          </Link>
        </Button>
      </div>
    </div>
  );
}

/* ============ Hero ============ */

function TodayHero({ pulse }: { pulse: NonNullable<ReturnType<typeof useMealsPulse>['data']> }) {
  const { todayMeals, percentPlanned } = pulse.plan;
  const hasMeals = todayMeals.length > 0 && todayMeals.some((m) => m.label);

  return (
    <Card className="overflow-hidden border-0 shadow-lg bg-gradient-to-br from-amber-500 via-orange-500 to-amber-600 text-white">
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest opacity-90">
              {hasMeals ? "Today's menu" : "Nothing planned for today"}
            </div>
            <div className="text-2xl md:text-3xl font-bold tracking-tight mt-1">
              {new Date(pulse.today + 'T00:00:00Z').toLocaleDateString('en-KE', {
                weekday: 'long', month: 'long', day: 'numeric',
              })}
            </div>
          </div>
          <Flame className="w-6 h-6 opacity-80 flex-shrink-0" />
        </div>

        {hasMeals ? (
          <div className="space-y-2">
            {todayMeals.filter((m) => m.label).map((m) => {
              const Icon = MEAL_ICON[m.mealType];
              return (
                <div key={m.mealType} className="flex items-center gap-2 text-sm">
                  <Icon className="w-4 h-4 opacity-80" />
                  <span className="font-semibold opacity-90 w-20">{MEAL_LABEL[m.mealType]}</span>
                  <span className="opacity-95">{m.label}</span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-sm opacity-85">
            {percentPlanned === 0
              ? "Head to Plan to pick meals for the week."
              : `You've planned ${percentPlanned}% of this week — but not today yet.`}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ============ Stat card ============ */

function StatCard({
  label, value, sub, accent, linkTo,
}: {
  label: string; value: string; sub: string;
  accent: 'amber' | 'orange' | 'rose' | 'emerald';
  linkTo?: string;
}) {
  const accentClass = {
    amber: 'bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300',
    orange: 'bg-orange-100 dark:bg-orange-950/40 text-orange-700 dark:text-orange-300',
    rose: 'bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300',
    emerald: 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300',
  }[accent];

  const content = (
    <Card>
      <CardContent className="p-3">
        <div className={cn('text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded inline-block', accentClass)}>
          {label}
        </div>
        <div className="text-xl md:text-2xl font-bold tabular-nums text-foreground mt-1.5">
          {value}
        </div>
        <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>
      </CardContent>
    </Card>
  );

  return linkTo ? <Link to={linkTo} className="block hover:opacity-80 transition-opacity">{content}</Link> : content;
}

/* ============ Small inline icons ============ */

function CalendarIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );
}
function BookIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  );
}
